/**
 * Confirmação de ação irreversível (manual §10).
 *
 * Regras que este componente impõe, e que não são negociáveis:
 * - O texto **nomeia o impacto concreto**, não um genérico "tem certeza?".
 * - O botão de confirmação é o de perigo; o de cancelar é secundário.
 * - **O foco inicial é o Cancelar.** Um Enter distraído não deve destruir nada.
 * - `Esc` fecha, e o foco fica preso dentro do modal (ambos vêm do Radix).
 */
import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';

export function ConfirmarAcao({
  aberto,
  onAbrir,
  titulo,
  impacto,
  rotuloConfirmar,
  onConfirmar,
  ocupado = false,
}: {
  aberto: boolean;
  onAbrir: (v: boolean) => void;
  titulo: string;
  /** Diga o que vai acontecer, com nomes e números. Nunca "esta ação é irreversível". */
  impacto: ReactNode;
  rotuloConfirmar: string;
  onConfirmar: () => void;
  ocupado?: boolean;
}) {
  const cancelar = useRef<HTMLButtonElement>(null);

  return (
    <Dialog.Root open={aberto} onOpenChange={onAbrir}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[var(--z-backdrop)] bg-[rgba(13,16,23,.6)] backdrop-blur-sm" />
        <Dialog.Content
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            cancelar.current?.focus();
          }}
          className="fixed left-1/2 top-1/2 z-[var(--z-modal)] w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-surface p-6 shadow-[var(--shadow-lg)]"
        >
          <Dialog.Title className="tipo-subtitulo text-text">{titulo}</Dialog.Title>
          <Dialog.Description asChild>
            <div className="mt-3 text-[13px] leading-normal text-text-secondary">{impacto}</div>
          </Dialog.Description>

          <div className="mt-6 flex justify-end gap-3">
            <Button ref={cancelar} variant="secundario" onClick={() => onAbrir(false)}>
              Cancelar
            </Button>
            <Button variant="perigo" onClick={onConfirmar} disabled={ocupado}>
              {ocupado ? <Spinner /> : rotuloConfirmar}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
